import {attr, Component, handler} from "../Component";
import { element } from "../CustomElement";

import { Dialog } from "../flipp-dialog/Dialog";
import { AuthProvider, ToastLevel } from "../../definitions/CommonDefinitions";

import {inject} from "../../services/di";
import {AuthenticationService} from "../../services/AuthenticationService";
import {EventService} from "../../services/EventService";
import {AccountsService} from "../../services/AccountsService";
import {LocationService} from "../../services/LocationService";
import {AnalyticsService} from "../../services/AnalyticsService";
import {ConfigService} from "../../services/ConfigService";
import {ToastService} from "../../services/ToastService";
import {LoginSource} from "../../services/definitions/IAnalyticsService";
import {i18n} from "../../i18n/I18n";
import { EMAIL_IS_MISSING } from "../../services/auth/FacebookAuthenticator";
import { icons } from "../../icons/Icons";
import { I18nLink } from "../flipp-i18n-link/I18nLink";
import { LinkService } from "../../services/LinkService";

export type LoginContext = "coupon_page" | "coupon_details" | "signin" | "flyer_item" | "share_dialog" | "shopping_list";

interface LoginButtonProps extends JSX.HTMLProps {
	provider: AuthProvider;
	context: LoginContext;
	round?: boolean;
	isDisabled?: string;
	buttonClick?: () => void;
}

@element("flipp-login-button")
export class LoginButton extends Component<LoginButtonProps, {}> {

	@inject auth: AuthenticationService;
	@inject eventService: EventService;
	@inject accounts: AccountsService;
	@inject location: LocationService;
	@inject analytics: AnalyticsService;
	@inject config: ConfigService;
	@inject toast: ToastService;

	@attr provider: AuthProvider;
	@attr context: LoginContext;
	@attr round: boolean;
	@attr isDisabled: string;

	@handler buttonClick: () => void;

	refs: {
		emailConfirm: Dialog;
		loginErrorDialog: Dialog;
		btn: HTMLElement;
	};

	state: {
		isDisabled: boolean;
	};

	onAttach() {
		super.onAttach();
		this.state.isDisabled = true;
	}

	onAttributeChange(name: string, oldVal: string, newVal: string) {
		super.onAttributeChange(name, oldVal, newVal);
		if (this.refs.btn && name === "isdisabled") {
			if (newVal === "true") {
				this.state.isDisabled = true;
			} else {
				this.state.isDisabled = false;
			}
		}
	}

	render() {
		let signinImg = "";
		if (this.provider === AuthProvider.GOOGLE) {
			signinImg = this.round ? "google-round.svg" : `google.png`;
		} else {
			signinImg = this.round ? "facebook-round.svg" : `fb.png`;
		}
		return <span>
				<button classList={{
					"facebook": this.provider === AuthProvider.FACEBOOK,
					"google": this.provider === AuthProvider.GOOGLE,
					"round": this.round
				}} ref="btn" onClick={this.handleLogin_}>
					<img class={this.round ? "round" : ""} src={`https://f.wishabi.net/creative/Distribution/images/${signinImg}`} alt="" />
					<span>
						{this.provider === AuthProvider.GOOGLE ? i18n("continue_with_google") :
							this.provider === AuthProvider.FACEBOOK ? i18n("continue_with_facebook") : null}
					</span>
				</button>
				{this.renderLoginErrorDialog()}
			</span>;
	}

	renderLoginErrorDialog() {
		return <Dialog class="login-error-dialog" ref="loginErrorDialog">
					<div class="content-wrapper" flex-vertical>
						<div class="logo" flex-horizontal>
							<h2 ref="formSignInHeader">{i18n("signin_email_missing")}</h2>
							<div ref="flippLogo">{icons.flippLogo}
							</div>
						</div>
						<button onClick={this.handleDialogClose_} class="close">
							{icons.x}
						</button>
					</div>
		</Dialog>;
	}

	private handleDialogClose_ = (e: Event) => {
		this.refs.loginErrorDialog.close();
		this.eventService.fireAuthEvent("login", null, this);
	}

	private handleLogin_ = (e: Event) => {
		if (this.state.isDisabled) {
			this.buttonClick();
			return;
		}
		const target = this.context === "coupon_page" ? window : this;
		this.auth.login(this.provider, target).then(() => {
			this.analytics.setAccountGuid(this.auth.user.token.user_id);
			this.eventService.fireAuthEvent("login", null, target);
			this.toast.fireToastEvent(i18n("signin_success"), ToastLevel.OK, true, target);
			this.analytics.trackSignin(this.auth.user.token.user_id, this.provider, this.getLoginSource_());
			if (this.context === "signin") {
				const linkService = LinkService.getInstance();
				linkService.execute("weekly_ads", this);
			} else {
				this.eventService.redirectToForm();
			}
		}).catch((e: Error) => {
			if (e.message === EMAIL_IS_MISSING) {
				this.refs.loginErrorDialog.showModal();
			} else {
				this.toast.fireToastEvent(i18n("signin_error"), ToastLevel.ERROR, true, target);
				this.eventService.fireAuthEvent("error", e.message, target);
			}
		});
	}

	private getLoginSource_ = (): LoginSource => {
		switch (this.context) {
			case "coupon_page": return LoginSource.CouponPage;
			case "signin": return LoginSource.Signin;
			case "flyer_item": return LoginSource.FlyerItem;
			case "coupon_details": return LoginSource.CouponDetails;
			case "share_dialog": return LoginSource.ShareDialog;
			case "shopping_list": return LoginSource.ShoppingList;
		}
	}
}
